// Print-to-PDF report builder.
//
// A property card can only ever show a summary — the full picture (every
// violation, every maintenance ticket, the whole rule-set) does not fit and
// should not try to. That detail goes here instead: one document the tenant
// saves via the browser's "Save as PDF".
//
// Deliberately no PDF library. The same print-window approach already ships in
// the rental agreement screen, it costs zero dependencies, and it renders with
// real CSS instead of hand-placed coordinates.

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const reportDate = formatDate;

export const reportMoney = (value) =>
  `PKR ${Number(value || 0).toLocaleString()}`;

const renderPairs = (rows = []) => {
  const visible = rows.filter((row) => row && row.value !== undefined);
  if (!visible.length) return "";

  return `<dl class="pairs">${visible
    .map(
      (row) => `<div class="pair">
        <dt>${escapeHtml(row.label)}</dt>
        <dd>${escapeHtml(row.value === "" ? "—" : row.value)}</dd>
      </div>`
    )
    .join("")}</dl>`;
};

const renderTable = (columns = [], rows = []) => {
  if (!rows.length) return "";

  return `<table>
    <thead><tr>${columns
      .map((column) => `<th>${escapeHtml(column)}</th>`)
      .join("")}</tr></thead>
    <tbody>${rows
      .map(
        (row) =>
          `<tr>${row
            .map((cell) => `<td>${escapeHtml(cell === "" ? "—" : cell)}</td>`)
            .join("")}</tr>`
      )
      .join("")}</tbody>
  </table>`;
};

const renderSection = (section) => {
  if (!section) return "";

  let body = "";
  if (section.type === "table") {
    body = renderTable(section.columns, section.rows);
  } else if (section.type === "text") {
    body = section.text
      ? `<p class="para">${escapeHtml(section.text)}</p>`
      : "";
  } else {
    body = renderPairs(section.rows);
  }

  if (!body) {
    body = `<p class="empty">${escapeHtml(
      section.empty || "Nothing recorded."
    )}</p>`;
  }

  return `<section class="block">
    <h2>${escapeHtml(section.heading)}</h2>
    ${
      section.note
        ? `<p class="note">${escapeHtml(section.note)}</p>`
        : ""
    }
    ${body}
  </section>`;
};

const renderStats = (stats = []) => {
  if (!stats.length) return "";

  return `<div class="stats">${stats
    .map(
      (stat) => `<div class="stat">
        <span class="stat-value">${escapeHtml(stat.value)}</span>
        <span class="stat-label">${escapeHtml(stat.label)}</span>
      </div>`
    )
    .join("")}</div>`;
};

/**
 * Opens a print-ready report in a new window and triggers the print dialog.
 *
 * @param {object}   report
 * @param {string}   report.title       Document heading (usually the flat name).
 * @param {string}   report.subtitle    Address or one-line context.
 * @param {string}   report.accent      Brand colour to tint the header with.
 * @param {Array}    report.stats       [{ label, value }] headline figures.
 * @param {Array}    report.sections    See renderSection for the accepted shapes.
 * @returns {boolean} false when the browser blocked the pop-up.
 */
export function openPropertyReport({
  title,
  subtitle = "",
  accent = "#2563eb",
  stats = [],
  sections = [],
}) {
  const printWindow = window.open("", "_blank", "width=980,height=800");
  if (!printWindow) return false;

  const generatedAt = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  printWindow.document.write(`<!DOCTYPE html><html><head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)} – OTS Report</title>
    <style>
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 34px 42px 48px;
        background: #fff;
        color: #14203a;
        font: 400 13px/1.55 "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      header {
        border-bottom: 3px solid ${accent};
        padding-bottom: 16px;
        margin-bottom: 22px;
      }
      .brand {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 1.6px;
        text-transform: uppercase;
        color: ${accent};
      }
      h1 { margin: 6px 0 4px; font-size: 26px; line-height: 1.2; }
      .subtitle { color: #5b6780; font-size: 13px; }
      .generated { margin-top: 10px; font-size: 11px; color: #8a93a6; }

      .stats {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-bottom: 26px;
      }
      .stat {
        flex: 1 1 150px;
        border: 1px solid #e4e8f0;
        border-left: 3px solid ${accent};
        border-radius: 8px;
        padding: 11px 14px;
      }
      .stat-value { display: block; font-size: 19px; font-weight: 700; }
      .stat-label {
        display: block;
        margin-top: 2px;
        font-size: 10.5px;
        letter-spacing: 0.7px;
        text-transform: uppercase;
        color: #7a839a;
      }

      .block { margin-bottom: 26px; page-break-inside: avoid; }
      h2 {
        margin: 0 0 4px;
        font-size: 13px;
        letter-spacing: 1.1px;
        text-transform: uppercase;
        color: ${accent};
      }
      .note { margin: 0 0 10px; font-size: 11.5px; color: #7a839a; }
      .para { margin: 8px 0 0; white-space: pre-wrap; }
      .empty { margin: 8px 0 0; color: #98a1b4; font-style: italic; }

      .pairs {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0 26px;
        margin: 10px 0 0;
      }
      .pair {
        display: flex;
        justify-content: space-between;
        gap: 14px;
        padding: 7px 0;
        border-bottom: 1px solid #eef1f6;
      }
      dt { color: #5b6780; }
      dd { margin: 0; font-weight: 600; text-align: right; }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 10px;
        font-size: 12px;
      }
      th {
        text-align: left;
        padding: 8px 10px;
        background: #f5f7fb;
        border-bottom: 1px solid #e4e8f0;
        font-size: 10.5px;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        color: #5b6780;
      }
      td {
        padding: 8px 10px;
        border-bottom: 1px solid #eef1f6;
        vertical-align: top;
      }
      tr { page-break-inside: avoid; }

      footer {
        margin-top: 30px;
        padding-top: 12px;
        border-top: 1px solid #e4e8f0;
        font-size: 10.5px;
        color: #98a1b4;
        text-align: center;
      }

      @page { margin: 14mm; }
      @media print { body { padding: 0; } }
    </style>
  </head><body>
    <header>
      <div class="brand">Owner Tenant Solution</div>
      <h1>${escapeHtml(title)}</h1>
      ${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ""}
      <div class="generated">Generated ${escapeHtml(generatedAt)}</div>
    </header>
    ${renderStats(stats)}
    ${sections.map(renderSection).join("")}
    <footer>
      This report reflects the data recorded on OTS at the time of generation.
    </footer>
    <script>
      window.onload = function () {
        setTimeout(function () {
          window.print();
          window.onafterprint = function () { window.close(); };
        }, 350);
      };
    </script>
  </body></html>`);

  printWindow.document.close();
  return true;
}
